/**
 * ProfilePostList — the tab panel under a profile card. Owns the same post
 * interaction stack as the feed (comments, likes, media viewer, post menu) but
 * reads its posts from useUserPosts instead of FeedContext.
 *
 * `?post=<id>` opens that one post over the list, which is how an edit started
 * from a post viewer gets back to the post it started on.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import feedService from '@wlb/shared/api/services/feedService';
import profileService from '@wlb/shared/api/services/profileService';
import { ToastService } from '@wlb/shared/platform/notifier';
import { normalizeUserGuid } from '@wlb/shared/utils/navigateToUserProfile';
import type { UseUserPostsResult } from '@wlb/shared/hooks/useUserPosts';
import type { FeedPost } from '@wlb/shared/types/post';
import type { Profile } from '@wlb/shared/types';
import { InfiniteScrollFooter } from '../../components/ui/InfiniteScrollFooter';
import { ListStatus } from '../../components/ui/ListStatus';
import { PostCard } from '../feed/PostCard';
import { PostListSkeleton } from '../feed/PostListSkeleton';
import { CommentsModal } from '../feed/CommentsModal';
import { LikesModal } from '../feed/LikesModal';
import { PostOptionsModal } from '../feed/PostOptionsModal';
import type { PostOptionKey } from '../feed/postOptions';
import { ReportPostModal } from '../feed/ReportPostModal';
import { SharePostModal } from '../feed/SharePostModal';
import { ConfirmModal } from '../feed/ConfirmModal';
import { BlockUserModal } from '../feed/BlockUserModal';
import { resolvePostImageUri } from '../feed/postMedia';
import {
  FOCUSED_POST_PARAM,
  editPostRouteState,
  focusedPostLocation,
} from '../createPost/createPostRoute';
import { useRestoreScrollOnReturn } from '../createPost/useRestoreScrollOnReturn';
import { getPageScrollY } from '../../utils/pageScrollLock';
import { useOpenDirectConversation } from '../messages/useOpenDirectConversation';
import { useOpenUserProfile } from './useOpenUserProfile';
import '../../components/ui/skeleton.css';
import '../feed/feed.css';
import './profile.css';

interface ProfilePostListProps {
  posts: UseUserPostsResult;
  /** The signed-in user's profile — decides which menu options a post gets. */
  viewerProfile: Profile | null;
  emptyMessage: string;
  /**
   * Overrides the save/unsave menu action. Saved Posts drops the row instead of
   * flipping the flag, so it owns both the API call and the list update.
   */
  onToggleSave?: (post: FeedPost, nextSaved: boolean) => void;
}

export function ProfilePostList({
  posts,
  viewerProfile,
  emptyMessage,
  onToggleSave,
}: ProfilePostListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const openUserProfile = useOpenUserProfile();
  const { openConversation } = useOpenDirectConversation();
  const [commentPost, setCommentPost] = useState<FeedPost | null>(null);
  const [likesPost, setLikesPost] = useState<FeedPost | null>(null);
  const [mediaPost, setMediaPost] = useState<FeedPost | null>(null);
  const [mediaIndex, setMediaIndex] = useState(0);
  // Stable so PostCard's memo holds while pages are appended — see PostCard.
  const handleMediaClick = useCallback((post: FeedPost, index?: number) => {
    setMediaPost(post);
    setMediaIndex(index ?? 0);
  }, []);

  // `?post=<id>` reopens a single post over the list. The composer writes it when
  // an edit starts from an open post viewer, so saving returns to that post
  // rather than to the bare list.
  const [searchParams, setSearchParams] = useSearchParams();
  const focusedPostId = searchParams.get(FOCUSED_POST_PARAM);
  const [focusedPost, setFocusedPost] = useState<FeedPost | null>(null);
  // Guards against reopening: once an id has been resolved the list refreshing
  // must not push the modal back up after the user closed it.
  const resolvedFocusRef = useRef<string | null>(null);
  // Lets an in-flight lookup see whether the URL still asks for that post.
  const focusedPostIdRef = useRef(focusedPostId);
  focusedPostIdRef.current = focusedPostId;
  const [menuPost, setMenuPost] = useState<FeedPost | null>(null);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  const [reportPost, setReportPost] = useState<FeedPost | null>(null);
  const [sharePostTarget, setSharePostTarget] = useState<FeedPost | null>(null);
  const [deleteTargetPost, setDeleteTargetPost] = useState<FeedPost | null>(null);
  const [blockTargetPost, setBlockTargetPost] = useState<FeedPost | null>(null);
  const [blockLoading, setBlockLoading] = useState(false);

  const closeMenu = () => {
    setMenuPost(null);
    setMenuAnchorRect(null);
  };

  // Stable so PostCard's memo holds while pages are appended — see PostCard.
  const handleCardMenu = useCallback((target: FeedPost, anchorRect: DOMRect) => {
    setMenuPost(target);
    setMenuAnchorRect(anchorRect);
  }, []);

  const likeUserPost = posts.likePost;
  const handleLike = useCallback(
    (postId: string) => {
      likeUserPost(postId).catch(() => undefined);
    },
    [likeUserPost],
  );

  const clearFocusedPost = useCallback(() => {
    setFocusedPost(null);
    if (!searchParams.has(FOCUSED_POST_PARAM)) return;
    const next = new URLSearchParams(searchParams);
    next.delete(FOCUSED_POST_PARAM);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!focusedPostId || resolvedFocusRef.current === focusedPostId) return;

    const listed = posts.posts.find(post => post.id === focusedPostId);
    if (listed) {
      resolvedFocusRef.current = focusedPostId;
      setFocusedPost(listed);
      return;
    }
    // The loaded tab may not hold the post — another tab, or a later page — so
    // fall back to GetPost the way the notification list does.
    if (!posts.loaded) return;
    resolvedFocusRef.current = focusedPostId;
    const notFound = () => {
      ToastService.show({ type: 'error', message: t('home.notifications.postNotFound') });
      clearFocusedPost();
    };
    feedService
      .getPostDetails(focusedPostId)
      .then(response => {
        // Dropped when the user closed the viewer while the lookup was running.
        if (focusedPostIdRef.current !== focusedPostId) return;
        if (response.success && response.data) {
          setFocusedPost(response.data.post);
        } else {
          notFound();
        }
      })
      .catch(() => {
        if (focusedPostIdRef.current !== focusedPostId) return;
        notFound();
      });
  }, [focusedPostId, posts.posts, posts.loaded, clearFocusedPost, t]);

  /** The post currently open on its own, if any — an edit returns to it. */
  const openViewerPostId = mediaPost?.id ?? focusedPost?.id ?? commentPost?.id ?? null;

  // Keeps the place in the list an edit was started from; the viewer that may
  // open over it comes from `?post=` above.
  useRestoreScrollOnReturn(posts.posts.length > 0);

  const handleMenuSelect = async (action: PostOptionKey) => {
    if (!menuPost) return;
    const currentPost = menuPost;
    closeMenu();

    switch (action) {
      case 'save': {
        const shouldSave = !currentPost.saved;
        if (onToggleSave) {
          onToggleSave(currentPost, shouldSave);
          break;
        }
        posts.patchPost(currentPost.id, { saved: shouldSave });
        const result = await feedService.savePost(currentPost.id, shouldSave);
        if (!result.success) posts.patchPost(currentPost.id, { saved: !shouldSave });
        break;
      }
      case 'message':
        await openConversation({
          userId: currentPost.userId ?? 0,
          name: currentPost.username,
          avatar: typeof currentPost.avatar === 'string' ? currentPost.avatar : null,
        });
        break;
      case 'profile': {
        // Only the GUID resolves a profile; a numeric userId dead-ends on the
        // "unavailable" screen. The hook also routes own posts to /profile.
        let authorGuid = normalizeUserGuid(currentPost.profileUserGuid);
        if (!authorGuid) {
          authorGuid = await feedService.resolvePostAuthorGuid(currentPost.id);
          // Keeps the author name and photo clickable from here on.
          if (authorGuid) posts.patchPost(currentPost.id, { profileUserGuid: authorGuid });
        }
        openUserProfile(authorGuid);
        break;
      }
      case 'report':
        setReportPost(currentPost);
        break;
      case 'block':
        setBlockTargetPost(currentPost);
        break;
      case 'edit': {
        // Started from the post's own view, the save has to land back on that
        // post; started from the list, it lands on the list as before.
        const from =
          openViewerPostId === currentPost.id
            ? focusedPostLocation(location, currentPost.id)
            : location;
        navigate('/create', {
          // The options menu still pins the body, so the offset comes from the lock.
          state: editPostRouteState(currentPost, from, getPageScrollY()),
        });
        break;
      }
      case 'delete':
        setDeleteTargetPost(currentPost);
        break;
      default:
        break;
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetPost) return;
    const target = deleteTargetPost;
    setDeleteTargetPost(null);
    posts.removePost(target.id);
    const result = await feedService.deletePost(target.id);
    if (!result.success) {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
      posts.refresh();
    }
  };

  const handleConfirmBlock = async () => {
    if (!blockTargetPost) return;
    const targetUserId = blockTargetPost.userId;
    const username = blockTargetPost.username;
    if (targetUserId == null) {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
      return;
    }
    setBlockLoading(true);
    const shouldBlock = !blockTargetPost.isBlocked;
    const result = await profileService.setBlockRelation(targetUserId, shouldBlock);
    setBlockLoading(false);
    if (!result.success) {
      ToastService.show({ type: 'error', message: result.message || t('common.errors.generic') });
      return;
    }
    setBlockTargetPost(null);
    ToastService.show({
      type: 'success',
      message: shouldBlock
        ? t('home.postOptions.blockSuccessMessage', { username })
        : t('home.postOptions.unblockSuccessMessage', { username }),
    });
    posts.refresh();
  };

  const renderContent = () => {
    if (posts.loading && posts.posts.length === 0) {
      return <PostListSkeleton count={2} />;
    }

    if (posts.error && posts.posts.length === 0) {
      return (
        <ListStatus
          variant="error"
          message={posts.error}
          onAction={posts.refresh}
          busy={posts.loading}
        />
      );
    }

    // Not answered yet — the loader stays instead of an "empty" claim.
    if (!posts.loaded && posts.posts.length === 0) {
      return <PostListSkeleton count={2} />;
    }

    if (posts.posts.length === 0) {
      return (
        <ListStatus
          variant="empty"
          title={emptyMessage}
          onAction={posts.refresh}
          busy={posts.loading}
        />
      );
    }

    return (
      <>
        <div className="feed-list skeleton-fade-in">
          {posts.posts.map(post => (
            <PostCard
              key={post.id}
              post={post}
              onLike={handleLike}
              onComment={setCommentPost}
              onShare={setSharePostTarget}
              onLikes={setLikesPost}
              onMediaClick={handleMediaClick}
              onMenu={handleCardMenu}
            />
          ))}
        </div>
        {/* A failure with rows already on screen belongs under the list: retry
            resumes pagination when there is more to fetch, otherwise it reloads. */}
        {posts.error ? (
          <ListStatus
            variant="error"
            layout="inline"
            message={posts.error}
            onAction={posts.hasNextPage ? posts.fetchNextPage : posts.refresh}
            busy={posts.isFetchingMore || posts.loading}
          />
        ) : (
          <InfiniteScrollFooter
            hasNextPage={posts.hasNextPage}
            isFetchingMore={posts.isFetchingMore}
            onLoadMore={posts.fetchNextPage}
            itemCount={posts.posts.length}
          />
        )}
      </>
    );
  };

  return (
    <div className="profile-posts">
      {renderContent()}
      {commentPost && (
        <CommentsModal
          post={commentPost}
          initialView="comments"
          onClose={() => setCommentPost(null)}
          onCommentAdded={() =>
            posts.patchPost(commentPost.id, { comments: (commentPost.comments || 0) + 1 })
          }
          onCommentCountChange={delta =>
            posts.patchPost(commentPost.id, { comments: Math.max(0, (commentPost.comments || 0) + delta) })
          }
          onMenu={(target, anchorRect) => { setMenuPost(target); setMenuAnchorRect(anchorRect); }}
        />
      )}
      {focusedPost && (
        <CommentsModal
          post={focusedPost}
          fullView
          onClose={clearFocusedPost}
          onCommentAdded={() =>
            posts.patchPost(focusedPost.id, { comments: (focusedPost.comments || 0) + 1 })
          }
          onCommentCountChange={delta =>
            posts.patchPost(focusedPost.id, { comments: Math.max(0, (focusedPost.comments || 0) + delta) })
          }
          onMenu={(target, anchorRect) => { setMenuPost(target); setMenuAnchorRect(anchorRect); }}
        />
      )}
      {likesPost && <LikesModal postId={likesPost.id} onClose={() => setLikesPost(null)} />}
      {mediaPost && (
        <CommentsModal
          post={mediaPost}
          fullView
          mediaViewer
          initialMediaIndex={mediaIndex}
          onClose={() => setMediaPost(null)}
          onCommentAdded={() =>
            posts.patchPost(mediaPost.id, { comments: (mediaPost.comments || 0) + 1 })
          }
          onCommentCountChange={delta =>
            posts.patchPost(mediaPost.id, { comments: Math.max(0, (mediaPost.comments || 0) + delta) })
          }
          onMenu={(target, anchorRect) => { setMenuPost(target); setMenuAnchorRect(anchorRect); }}
        />
      )}
      <PostOptionsModal
        post={menuPost}
        profile={viewerProfile}
        anchorRect={menuAnchorRect}
        onClose={closeMenu}
        onSelect={handleMenuSelect}
      />
      {reportPost && <ReportPostModal post={reportPost} onClose={() => setReportPost(null)} />}
      {sharePostTarget && (
        <SharePostModal post={sharePostTarget} onClose={() => setSharePostTarget(null)} />
      )}
      {deleteTargetPost && (
        <ConfirmModal
          title={t('home.postOptions.deleteConfirmTitle')}
          message={t('home.postOptions.deleteConfirmMessage')}
          confirmLabel={t('home.postOptions.deleteConfirm')}
          cancelLabel={t('home.postOptions.deleteConfirmCancel')}
          mediaUrl={resolvePostImageUri(deleteTargetPost.image)}
          onClose={() => setDeleteTargetPost(null)}
          onConfirm={() => { handleConfirmDelete().catch(() => undefined); }}
          showIcon
        />
      )}
      {blockTargetPost && (
        <BlockUserModal
          username={blockTargetPost.username}
          avatarUrl={typeof blockTargetPost.avatar === 'string' ? blockTargetPost.avatar : null}
          seed={blockTargetPost.profileUserGuid ?? blockTargetPost.userId}
          isBlocked={blockTargetPost.isBlocked}
          loading={blockLoading}
          onClose={() => setBlockTargetPost(null)}
          onConfirm={() => { handleConfirmBlock().catch(() => undefined); }}
        />
      )}
    </div>
  );
}
